import type {
  ColumnOrderState,
  ColumnVisibilityState,
} from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { useServerStorage } from 'utils/useServerStorage';

import type { ConversationsTableDensity } from '../conversationsTable.types';

export type UseConversationsTableLayoutOptions = {
  /** Whether the user can customise the table; no layout is returned otherwise. */
  readonly isEnabled: boolean;
};

/** The user's saved layout of the conversations table, and how to change it. */
export type UseConversationsTableLayoutResult = {
  /** How much space the rows take up. */
  readonly density: ConversationsTableDensity;
  /** The ids of the columns in the order they're shown. */
  readonly columnOrder: ColumnOrderState;
  /** Whether each column is shown, keyed by column id. */
  readonly columnVisibility: ColumnVisibilityState;
  /** Saves how much space the rows take up. */
  readonly setDensity: (density: ConversationsTableDensity) => void;
  /** Shows or hides a column. */
  readonly setIsColumnVisible: (columnId: string, isVisible: boolean) => void;
  /** Saves the order of the columns, given as column ids. */
  readonly setColumnOrder: (columnOrder: readonly string[]) => void;
  /** Clears the saved layout so the table goes back to its default columns. */
  readonly restoreDefaults: () => void;
};

/** Returns the conversations table layout saved in the user storage. */
export const useConversationsTableLayout = ({
  isEnabled,
}: UseConversationsTableLayoutOptions):
  | UseConversationsTableLayoutResult
  | undefined => {
  const { data, set } = useServerStorage('user');
  const table = isEnabled ? data.conversations?.table : undefined;
  const density = table?.density ?? 'default';
  const columns = table?.columns;
  const savedColumnOrder = table?.columnOrder;

  const columnOrder = useMemo(
    (): ColumnOrderState => [...(savedColumnOrder ?? [])],
    [savedColumnOrder],
  );

  const columnVisibility = useMemo(
    (): ColumnVisibilityState =>
      Object.fromEntries(
        Object.entries(columns ?? {}).map(([columnId, column]) => [
          columnId,
          !column.isHidden,
        ]),
      ),
    [columns],
  );

  const setDensity = useCallback(
    (nextDensity: ConversationsTableDensity) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.density = nextDensity;
        },
      });
    },
    [set],
  );

  const setIsColumnVisible = useCallback(
    (columnId: string, isVisible: boolean) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.columns ??= {};
          draft.table.columns[columnId] = {
            ...draft.table.columns[columnId],
            isHidden: !isVisible,
          };
        },
      });
    },
    [set],
  );

  const setColumnOrder = useCallback(
    (nextColumnOrder: readonly string[]) => {
      set({
        conversations: (draft) => {
          draft.table ??= {};
          draft.table.columnOrder = [...nextColumnOrder];
        },
      });
    },
    [set],
  );

  const restoreDefaults = useCallback(() => {
    set({
      conversations: (draft) => {
        draft.table = null;
      },
    });
  }, [set]);

  return useMemo(
    () =>
      isEnabled
        ? {
            density,
            setDensity,
            columnOrder,
            columnVisibility,
            setIsColumnVisible,
            setColumnOrder,
            restoreDefaults,
          }
        : undefined,
    [
      isEnabled,
      density,
      setDensity,
      columnOrder,
      columnVisibility,
      setIsColumnVisible,
      setColumnOrder,
      restoreDefaults,
    ],
  );
};
